import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, Users, Calendar, MapPin, Phone, CheckCircle2 } from "lucide-react";
import api, { universeLabel, resolveImage } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ProductCard from "@/components/ProductCard";
import Reveal from "@/components/Reveal";
import { Slider } from "@/components/ui/slider";
import { useI18n } from "@/lib/i18n";

export default function Universe() {
  const { slug } = useParams();
  const { lang } = useI18n();
  const [products, setProducts] = useState([]);
  const [universe, setUniverse] = useState(null);
  const [maxPrice, setMaxPrice] = useState(15000);
  const [selectedCats, setSelectedCats] = useState([]);
  const [sort, setSort] = useState("featured");

  useEffect(() => {
    Promise.all([
      api.get("/products", { params: { universe: slug } }),
      api.get("/universes"),
    ]).then(([p, u]) => {
      setProducts(p.data);
      setUniverse(u.data.find((x) => x.slug === slug));
    });
  }, [slug]);

  const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const today = WEEKDAYS[new Date().getDay()];

  const categories = useMemo(() => Array.from(new Set(products.map((p) => p.category))), [products]);

  const todaysSpecials = useMemo(
    () => (slug === "street_market" ? products.filter((p) => p.category === today) : []),
    [products, slug, today]
  );

  const filtered = useMemo(() => {
    let list = products.filter((p) => p.price <= maxPrice);
    if (selectedCats.length) list = list.filter((p) => selectedCats.includes(p.category));
    if (sort === "price_asc") list = [...list].sort((a, b) => a.price - b.price);
    if (sort === "price_desc") list = [...list].sort((a, b) => b.price - a.price);
    if (sort === "rating") list = [...list].sort((a, b) => b.rating - a.rating);
    // For Lunch Specials universe with default sort, surface today's items first
    if (slug === "street_market" && sort === "featured") {
      list = [...list].sort((a, b) => (a.category === today ? -1 : 0) - (b.category === today ? -1 : 0));
    }
    return list;
  }, [products, maxPrice, selectedCats, sort, slug, today]);

  const toggleCat = (cat) => {
    setSelectedCats((prev) => (prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]));
  };

  return (
    <div className="bg-white text-[var(--dsf-text)] min-h-screen">
      <Header />

      {/* Hero */}
      <section className="pt-20" data-testid="universe-hero">
        <div className="relative aspect-[21/9] md:aspect-[21/7]">
          <img src={resolveImage(universe?.image)} alt={universe?.name} className="absolute inset-0 w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--dd-green-deep)]/95 via-[var(--dd-green-deep)]/40 to-transparent" />
          <div className="relative z-10 h-full flex flex-col justify-end px-6 md:px-12 lg:px-20 py-12">
            <div className="text-[10px] uppercase tracking-[0.5em] text-[var(--dsf-orange)] font-bold mb-4 drop-shadow-md">{lang === "fr" ? "Univers" : "Universe"}</div>
            <h1 className="font-bold tracking-tight text-5xl md:text-7xl lg:text-8xl text-[var(--dsf-orange)] leading-[0.95] drop-shadow-lg">{universe?.name}</h1>
            <p className="text-[var(--dsf-orange)]/95 font-medium mt-4 max-w-xl text-lg drop-shadow-md">{lang === "fr" ? (universe?.tagline_fr || universe?.tagline) : universe?.tagline}</p>
          </div>
        </div>
      </section>

      {/* Today's Special banner — Lunch Specials only */}
      {slug === "street_market" && todaysSpecials.length > 0 && (
        <section className="px-6 md:px-12 lg:px-20 pt-12" data-testid="todays-special-banner">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-[var(--dd-coral)] via-[var(--dd-mustard-dark)] to-[var(--dd-mustard)] p-6 md:p-8">
            <div className="absolute -top-8 -right-8 w-44 h-44 rounded-full bg-white/10 blur-3xl" />
            <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
              <div>
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-white/85 font-bold mb-2">
                  <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                  {lang === "fr" ? `Aujourd'hui · ${today}` : `Today · ${today}`}
                </div>
                <h2 className="font-serif text-3xl md:text-4xl text-white leading-tight max-w-2xl">
                  {lang === "fr" ? "Le plat du jour" : "Today's special"}
                  <span className="italic"> {todaysSpecials.map((p) => p.name.replace(/^[A-Za-z]+ — /, "")).join(" · ")}</span>
                </h2>
                <div className="text-sm text-white/85 mt-2">
                  {lang === "fr" ? "Disponible uniquement aujourd'hui — commandez avant rupture." : "Available today only — order before we run out."}
                </div>
              </div>
              {todaysSpecials[0] && (
                <a href="#products-grid" className="shrink-0 bg-white text-[var(--dd-coral)] px-5 py-3 rounded-full text-sm font-bold uppercase tracking-wider shadow-md hover:scale-105 transition" data-testid="todays-cta">
                  {lang === "fr" ? "Voir le menu" : "See menu"} →
                </a>
              )}
            </div>
          </div>
        </section>
      )}

      <section id="products-grid" className="px-6 md:px-12 lg:px-20 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Filters */}
          <aside className="lg:col-span-3" data-testid="filters-panel">
            <div className="sticky top-28 space-y-8">
              <div>
                <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--dd-mustard)] font-bold mb-4">{lang === "fr" ? "Trier par" : "Sort by"}</div>
                <div className="space-y-2">
                  {[
                    { v: "featured", l: lang === "fr" ? "Recommandé" : "Featured" },
                    { v: "price_asc", l: lang === "fr" ? "Prix croissant" : "Price asc." },
                    { v: "price_desc", l: lang === "fr" ? "Prix décroissant" : "Price desc." },
                    { v: "rating", l: lang === "fr" ? "Mieux noté" : "Top rated" },
                  ].map((o) => (
                    <button
                      key={o.v}
                      onClick={() => setSort(o.v)}
                      className={`block w-full text-left text-sm py-1.5 ${sort === o.v ? "text-[var(--dd-mustard)] font-medium" : "text-[var(--dsf-text-secondary)] hover:text-[var(--dd-green)]"}`}
                      data-testid={`sort-${o.v}`}
                    >
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>

              <div className="border-t border-black/5 pt-8">
                <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--dd-mustard)] font-bold mb-4">{lang === "fr" ? "Prix maximum" : "Max price"}</div>
                <Slider
                  value={[maxPrice]}
                  min={5}
                  max={150}
                  step={1}
                  onValueChange={(v) => setMaxPrice(v[0])}
                  data-testid="price-slider"
                />
                <div className="text-sm font-medium mt-3 text-[var(--dd-green)]">${maxPrice}</div>
              </div>

              <div className="border-t border-black/5 pt-8">
                <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--dd-mustard)] font-bold mb-4">{lang === "fr" ? "Catégories" : "Categories"}</div>
                <div className="space-y-2">
                  {categories.map((c) => (
                    <label key={c} className="flex items-center gap-2 cursor-pointer text-sm" data-testid={`category-filter-${c}`}>
                      <input
                        type="checkbox"
                        checked={selectedCats.includes(c)}
                        onChange={() => toggleCat(c)}
                        className="accent-[var(--dsf-orange)] w-4 h-4"
                      />
                      <span className={selectedCats.includes(c) ? "text-[var(--dd-green)] font-medium" : "text-[var(--dsf-text-secondary)]"}>{c}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          {/* Products grid */}
          <div className="lg:col-span-9">
            <div className="flex items-center justify-between mb-8">
              <div className="text-sm text-[var(--dsf-text-muted)]" data-testid="products-count">
                {filtered.length} {lang === "fr" ? `produit${filtered.length > 1 ? "s" : ""}` : `item${filtered.length > 1 ? "s" : ""}`}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-x-4 sm:gap-x-6 gap-y-8">
              {filtered.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
            {filtered.length === 0 && (
              <div className="py-24 text-center text-[var(--dsf-text-muted)]">{lang === "fr" ? "Aucun produit ne correspond à vos critères." : "No items match your filters."}</div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
